import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { listDatasets } from '../../../services/datasets.js';

/** Asked for per request; the picker lists the whole catalogue. */
export const DATASET_PAGE_SIZE = 100;

/** A stop, so a backend that never stops paging cannot hang the picker. */
const MAX_DATASET_PAGES = 10;

/**
 * Every page of `/dataset/list`.
 *
 * The picker has no "browse more" affordance — the design lists the whole
 * catalogue — and one page of 20 was written when a workspace was expected
 * to hold a handful. Dev already holds on the order of a hundred, including
 * the datasets authored beside `ENTITY` widgets, so everything past the 20
 * most recent was simply missing (Bugbot #596).
 */
const listAllDatasets = async () => {
  const all = [];

  for (let page = 1; page <= MAX_DATASET_PAGES; page += 1) {
    // eslint-disable-next-line no-await-in-loop -- paging is sequential: the
    // next request is only worth making once this one says there is more.
    const { datasets, meta } = await listDatasets({
      page, pageSize: DATASET_PAGE_SIZE, orderBy: 'updated_at',
    });
    all.push(...datasets);

    const totalPages = meta?.totalPages ?? 0;
    const more = totalPages ? page < totalPages : datasets.length === DATASET_PAGE_SIZE;
    if (!more) break;
  }

  return all;
};

/**
 * Every dataset a widget may be built on, as the picker renders them
 * (CT-5191, made real by CT-5860).
 *
 * The design source of truth lists the whole (small) catalogue with no "browse
 * more" affordance, so this asks for one page big enough to hold it and still
 * reports `total` for anything that wants to count.
 *
 * ## It used to be four Salesforce seeds
 *
 * `datasetsService.js` served `Contacts by Tier`, `Deals Pipeline`, `Total
 * MRR` and `All Contacts` to every workspace, because no endpoint listed
 * datasets when the picker was built. `/v0/dashboard/dataset/list` does now,
 * so the seeds are gone — picking one of them produced a widget against
 * columns no dataset carried, which saved and then failed at execution.
 *
 * Two fields the list does not answer, and why they are what they are:
 *
 * - **`shape` is always `record_set`.** Under rev4 a published dataset *is* a
 *   plain record set — the aggregations live in the widget's bindings — and
 *   the widgets' own generated queries are never listed. So there is no
 *   grouped or single-value dataset to tell apart, and saying `record_set`
 *   is reading the contract rather than guessing.
 * - **`integration` is empty**, and the card drops the badge. A dataset
 *   belongs to a composite, not to a connector, and naming one would be
 *   inventing provenance.
 *
 * Only `PUBLISHED` rows are offered: a dataset in any other state has no
 * columns to bind to. The filter is this side's because `/dataset/list`
 * takes no status, which is also why `total` counts what is shown.
 */
export const useDatasets = () => {
  const [state, setState] = useState({ datasets: [], total: 0, error: null });
  const [resolvedKey, setResolvedKey] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);
  const fetchIdRef = useRef(0);

  const requestKey = `datasets|${reloadToken}`;
  const isLoading = resolvedKey !== requestKey;

  useEffect(() => {
    const fetchId = ++fetchIdRef.current;
    let mounted = true;

    listAllDatasets()
      .then((datasets) => {
        if (!mounted || fetchId !== fetchIdRef.current) return;
        const published = datasets.filter((dataset) => dataset.status === 'PUBLISHED');
        setState({
          datasets: published.map((dataset) => ({
            id: dataset.datasetUuid,
            // A dataset the builder published is named after its hash; the
            // code is the only thing a person can match it against.
            name: dataset.name || dataset.code,
            description: '',
            shape: 'record_set',
            integration: '',
            governed: true,
            compositeEntityUuid: dataset.compositeEntityUuid,
          })),
          // What the picker shows, not what the endpoint holds: the
          // unpublished rows were just filtered out here.
          total: published.length,
          error: null,
        });
      })
      .catch((error) => {
        if (!mounted || fetchId !== fetchIdRef.current) return;
        setState({ datasets: [], total: 0, error });
      })
      .finally(() => {
        if (mounted && fetchId === fetchIdRef.current) setResolvedKey(requestKey);
      });

    return () => {
      mounted = false;
    };
  }, [requestKey]);

  const refetch = useCallback(() => setReloadToken((n) => n + 1), []);

  return useMemo(
    () => ({
      datasets: state.datasets,
      total: state.total,
      isLoading,
      error: state.error,
      refetch,
    }),
    [isLoading, refetch, state.datasets, state.error, state.total],
  );
};

export default useDatasets;
